import React from 'react';
import { 
  X, Clock, Globe, CheckCircle, XCircle, ExternalLink, 
  Users, DollarSign, FileText, AlertCircle 
} from 'lucide-react';
import { Modal } from '../common/Modal';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Store, StoreCategory } from '../../types';

interface StoreDetailsProps {
  store: Store | null;
  isOpen: boolean;
  onClose: () => void;
}

export const StoreDetails: React.FC<StoreDetailsProps> = ({
  store,
  isOpen,
  onClose
}) => {
  if (!store) return null;

  const getCategoryColor = (category: StoreCategory) => {
    switch (category) {
      case 'streaming': return 'text-green-400 bg-green-500/10 border-green-500/20';
      case 'download': return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
      case 'social': return 'text-purple-400 bg-purple-500/10 border-purple-500/20';
      case 'radio': return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
      case 'gaming': return 'text-red-400 bg-red-500/10 border-red-500/20';
      default: return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
    }
  };

  const getCategoryLabel = (category: StoreCategory) => {
    switch (category) {
      case 'streaming': return 'Streaming Platform';
      case 'download': return 'Download Store';
      case 'social': return 'Social Media Platform';
      case 'radio': return 'Radio Platform';
      case 'gaming': return 'Gaming Platform';
      default: return 'Other Platform';
    }
  };

  const getStoreInfo = () => {
    const baseInfo = {
      'spotify': {
        website: 'https://spotify.com',
        payoutRate: '$0.003 - $0.005 per stream',
        audience: '400M+ users worldwide',
        requirements: [
          'High-quality audio files (minimum 16-bit/44.1kHz)',
          'Professional album artwork (minimum 1400x1400px)',
          'Complete metadata including ISRC codes',
          'No copyrighted material without proper licensing'
        ]
      },
      'apple-music': {
        website: 'https://music.apple.com',
        payoutRate: '$0.007 - $0.01 per stream',
        audience: '100M+ subscribers globally',
        requirements: [
          'Lossless audio quality preferred',
          'Square album artwork in RGB color space',
          'Accurate genre classification',
          'Artist verification for better visibility'
        ]
      },
      'youtube-music': {
        website: 'https://music.youtube.com',
        payoutRate: '$0.001 - $0.003 per stream',
        audience: '80M+ premium subscribers',
        requirements: [
          'Content ID registration available',
          'Music video support',
          'YouTube integration benefits',
          'Creator monetization options'
        ]
      }
    };

    return baseInfo[store.id as keyof typeof baseInfo] || {
      website: '#',
      payoutRate: 'Contact for rates',
      audience: 'Contact for audience data',
      requirements: [
        'Standard music distribution requirements',
        'Professional quality audio and artwork',
        'Complete and accurate metadata',
        'Rights ownership verification'
      ]
    };
  };

  const storeInfo = getStoreInfo();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" title="">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-6xl">{store.logo}</div>
            <div>
              <h2 className="text-2xl font-bold text-white font-display mb-2">
                {store.name}
              </h2>
              <div className="flex items-center space-x-3 mb-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getCategoryColor(store.category)}`}>
                  {getCategoryLabel(store.category)}
                </span>
                {store.isActive ? (
                  <div className="flex items-center space-x-1 text-green-400">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Active</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 text-red-400">
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm">Inactive</span>
                  </div>
                )}
              </div>
              <p className="text-gray-300 leading-relaxed max-w-2xl">
                {store.description}
              </p>
            </div>
          </div>
          
          <div className="flex space-x-2">
            <Button
              variant="outline"
              icon={ExternalLink}
              onClick={() => window.open(storeInfo.website, '_blank')}
            >
              Visit Store
            </Button>
            <Button
              variant={store.isActive ? "danger" : "primary"}
            >
              {store.isActive ? 'Disable' : 'Enable'} Distribution
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="text-center p-4">
            <Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <div className="text-lg font-bold text-white mb-1">
              {store.processingTime}
            </div>
            <div className="text-sm text-gray-400">Processing Time</div>
          </Card>
          <Card className="text-center p-4">
            <Globe className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <div className="text-lg font-bold text-white mb-1">
              {store.region.length} Region{store.region.length > 1 ? 's' : ''}
            </div>
            <div className="text-sm text-gray-400">Available In</div>
          </Card>
          <Card className="text-center p-4">
            <Users className="w-8 h-8 text-purple-400 mx-auto mb-2" />
            <div className="text-lg font-bold text-white mb-1">
              {storeInfo.audience.split(' ')[0]}
            </div>
            <div className="text-sm text-gray-400">User Base</div>
          </Card>
        </div>

        {/* Detailed Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Platform Details */}
          <Card>
            <h3 className="text-lg font-semibold text-white font-display mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Platform Details
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Category</p>
                <p className="text-white">{getCategoryLabel(store.category)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Available Regions</p>
                <div className="flex flex-wrap gap-2">
                  {store.region.map((region, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-700 rounded text-sm text-white"
                    >
                      {region}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Processing Time</p>
                <p className="text-white">{store.processingTime}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Audience</p>
                <p className="text-white">{storeInfo.audience}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Payout Rate</p>
                <p className="text-green-400 font-medium">{storeInfo.payoutRate}</p>
              </div>
            </div>
          </Card>

          {/* Requirements */}
          <Card>
            <h3 className="text-lg font-semibold text-white font-display mb-4 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Distribution Requirements
            </h3>
            <div className="space-y-3">
              {storeInfo.requirements.map((requirement, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-gold-400 rounded-full mt-2 flex-shrink-0" />
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {requirement}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Distribution Status */}
        {store.isActive ? (
          <Card className="bg-green-500/10 border-green-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <div>
                  <p className="font-medium text-green-400">Distribution Active</p>
                  <p className="text-sm text-gray-300 mt-1">
                    Your music will be distributed to {store.name} when you submit new releases.
                  </p>
                </div>
              </div>
              <Button variant="outline" className="text-red-400 border-red-400 hover:bg-red-500/10">
                Disable
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="bg-red-500/10 border-red-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <XCircle className="w-6 h-6 text-red-400" />
                <div>
                  <p className="font-medium text-red-400">Distribution Inactive</p>
                  <p className="text-sm text-gray-300 mt-1">
                    Enable distribution to {store.name} to include it in your releases.
                  </p>
                </div>
              </div>
              <Button>
                Enable Distribution
              </Button>
            </div>
          </Card>
        )}

        {/* Performance Metrics (if available) */}
        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4 flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Performance Overview
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-800/50 rounded-lg">
              <div className="text-xl font-bold text-white mb-1">-</div>
              <div className="text-sm text-gray-400">Total Streams</div>
            </div>
            <div className="text-center p-4 bg-gray-800/50 rounded-lg">
              <div className="text-xl font-bold text-white mb-1">-</div>
              <div className="text-sm text-gray-400">Total Revenue</div>
            </div>
            <div className="text-center p-4 bg-gray-800/50 rounded-lg">
              <div className="text-xl font-bold text-white mb-1">-</div>
              <div className="text-sm text-gray-400">Active Releases</div>
            </div>
          </div>
          <p className="text-sm text-gray-400 text-center mt-4">
            Performance data will be available after your first distribution to {store.name}.
          </p>
        </Card>
      </div>
    </Modal>
  );
};